/**
 * One-time automatic agent-pin state and its browser compatibility cache.
 *
 * The server remains the source of truth for the actual pin relation. This
 * Current servers persist this state per user. The local record remains a
 * rolling-deployment fallback for older bridges/servers and migrates into the
 * server record when that endpoint becomes available.
 */

import { fetchWithAuth, type AccessTokenGetter } from "@/lib/utils";

export interface AgentPinBootstrapState {
  defaultAgentInitialized: boolean;
  initializedPinnedByDefaultAgentIds: string[];
}

const STORAGE_KEY_PREFIX = "l9-agent-pin-bootstrap/v1";
const SERVER_STATE_URL = "/api/user-preferences/agent-pin-bootstrap";
const UNSUPPORTED_STATUSES = new Set([400, 404, 405, 501]);

function uniqueAgentIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const item of value) {
    if (typeof item !== "string" || !item || seen.has(item)) continue;
    seen.add(item);
    ids.push(item);
  }
  return ids;
}

export function emptyAgentPinBootstrapState(): AgentPinBootstrapState {
  return {
    defaultAgentInitialized: false,
    initializedPinnedByDefaultAgentIds: [],
  };
}

export function mergeAgentPinBootstrapStates(
  ...states: readonly AgentPinBootstrapState[]
): AgentPinBootstrapState {
  return {
    defaultAgentInitialized: states.some((state) => state.defaultAgentInitialized),
    initializedPinnedByDefaultAgentIds: uniqueAgentIds(
      states.flatMap((state) => state.initializedPinnedByDefaultAgentIds),
    ),
  };
}

function parseServerState(value: unknown): AgentPinBootstrapState | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (
    typeof record.default_agent_initialized !== "boolean"
    || !Array.isArray(record.initialized_pinned_by_default_agent_ids)
  ) return null;
  return {
    defaultAgentInitialized: record.default_agent_initialized,
    initializedPinnedByDefaultAgentIds: uniqueAgentIds(
      record.initialized_pinned_by_default_agent_ids,
    ),
  };
}

/** Return null when the independently deployed server does not support this state. */
export async function readServerAgentPinBootstrapState(
  getToken: AccessTokenGetter,
): Promise<AgentPinBootstrapState | null> {
  const response = await fetchWithAuth(
    SERVER_STATE_URL,
    { headers: {}, cache: "no-store" },
    getToken,
  );
  if (UNSUPPORTED_STATUSES.has(response.status)) return null;
  if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  const parsed = parseServerState(await response.json().catch(() => null));
  if (!parsed) throw new Error("Invalid agent pin bootstrap response");
  return parsed;
}

/** Monotonically add initialized defaults; false means an older server. */
export async function writeServerAgentPinBootstrapState(
  getToken: AccessTokenGetter,
  state: AgentPinBootstrapState,
): Promise<boolean> {
  const response = await fetchWithAuth(
    SERVER_STATE_URL,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        default_agent_initialized: state.defaultAgentInitialized,
        initialized_pinned_by_default_agent_ids: uniqueAgentIds(
          state.initializedPinnedByDefaultAgentIds,
        ),
      }),
    },
    getToken,
  );
  if (UNSUPPORTED_STATUSES.has(response.status)) return false;
  if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  return true;
}

export interface AgentPinBootstrapPlan {
  /** Marked agents absent from the initial pin snapshot and not previously offered. */
  markerIdsToPin: string[];
  /** Markers already present count as initialized without another write. */
  initializedPinnedByDefaultAgentIds: string[];
  /** Existing explicit pins suppress the legacy tenant-default write. */
  defaultAgentInitialized: boolean;
  /** Whether the caller should resolve and potentially pin the tenant default. */
  shouldResolveDefaultAgent: boolean;
}

/**
 * Compute the one-time bootstrap work from a confirmed server pin snapshot.
 * A marked agent that was already offered stays absent after a later explicit
 * unpin, while a newly introduced marker is still offered once.
 */
export function planAgentPinBootstrap(
  state: AgentPinBootstrapState,
  currentPinnedAgentIds: readonly string[],
  pinnedByDefaultAgentIds: readonly string[],
): AgentPinBootstrapPlan {
  const currentPins = new Set(uniqueAgentIds(currentPinnedAgentIds));
  // Only markers recorded during a prior bootstrap are known to be automatic.
  // A marker already present on this browser's first observed pin snapshot
  // still counts as an existing user choice, preserving legacy default-agent
  // semantics for established profiles.
  const previouslyInitializedMarkerSet = new Set(
    uniqueAgentIds(state.initializedPinnedByDefaultAgentIds),
  );
  const initializedMarkers = uniqueAgentIds([
    ...previouslyInitializedMarkerSet,
    ...pinnedByDefaultAgentIds.filter((agentId) => currentPins.has(agentId)),
  ]);
  const initializedMarkerSet = new Set(initializedMarkers);
  const markerIdsToPin = uniqueAgentIds(pinnedByDefaultAgentIds).filter((agentId) => (
    !initializedMarkerSet.has(agentId) && !currentPins.has(agentId)
  ));
  // If a first-run marker was pinned but resolving the generalist temporarily
  // failed, retry the generalist next load. A pre-existing pin (including a
  // manually pinned marker first seen before this feature) still suppresses
  // the legacy default exactly as before.
  const hasPreexistingUserPin = [...currentPins].some(
    (agentId) => !previouslyInitializedMarkerSet.has(agentId),
  );
  const defaultAgentInitialized = state.defaultAgentInitialized || hasPreexistingUserPin;

  return {
    markerIdsToPin,
    initializedPinnedByDefaultAgentIds: initializedMarkers,
    defaultAgentInitialized,
    shouldResolveDefaultAgent: !defaultAgentInitialized,
  };
}

/** Keep the marker local to a caller-provided browser-profile scope. */
export function agentPinBootstrapStorageKey(
  scope: string | null | undefined,
  userId: string | null | undefined,
): string {
  return `${STORAGE_KEY_PREFIX}:${encodeURIComponent(scope ?? "unknown")}:${encodeURIComponent(userId ?? "unknown")}`;
}

export function readAgentPinBootstrapState(storageKey: string): AgentPinBootstrapState {
  if (typeof window === "undefined") return emptyAgentPinBootstrapState();
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return emptyAgentPinBootstrapState();
    const value = JSON.parse(raw) as unknown;
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return emptyAgentPinBootstrapState();
    }
    const state = value as Record<string, unknown>;
    return {
      defaultAgentInitialized: state.defaultAgentInitialized === true,
      initializedPinnedByDefaultAgentIds: uniqueAgentIds(
        state.initializedPinnedByDefaultAgentIds,
      ),
    };
  } catch {
    // Private browsing and corrupt prior values should fall back to the safe
    // first-load behavior rather than making the sidebar unusable.
    return emptyAgentPinBootstrapState();
  }
}

export function writeAgentPinBootstrapState(
  storageKey: string,
  state: AgentPinBootstrapState,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify({
      defaultAgentInitialized: state.defaultAgentInitialized === true,
      initializedPinnedByDefaultAgentIds: uniqueAgentIds(
        state.initializedPinnedByDefaultAgentIds,
      ),
    }));
  } catch {
    // Storage is an enhancement for unpin persistence, never a prerequisite
    // for pinning or rendering the catalog.
  }
}
